import { ITEMS, } from '../data/items.js';
import { BIOME_IDS, BIOMES } from '../data/biomes.js';
import { silhouetteOf } from '../data/props.js';
import { tradeBlock } from './trade.js';
/**
 * The auction house (ADR 0035), as pure rules: the commission, the bounds,
 * and how a search typed by a player becomes the template ids the database
 * is asked for (an item's name is game data, not a column).
 */
/** Share of the price the house keeps and burns (ADR 0038). */
export const AUCTION_FEE_RATE = 0.05;
/** A lot stays up this long, then waits to be collected by its seller. */
export const AUCTION_DURATION_MS = 48 * 60 * 60_000;
/** Active lots one character may hold at once. */
export const AUCTION_MAX_LISTINGS = 10;
export const AUCTION_PAGE_SIZE = 50;
export const AUCTION_MAX_PRICE = 10_000_000;
/** An auction hall belongs to the biome of its village, not to the whole world room. */
export function auctionBiomeOf(prop) {
    return BIOME_IDS.find((id) => BIOMES[id].services.auction === prop.prop);
}
/** The visible facade under a click, including the roof above its footprint. */
export function auctionHouseAt(zone, at) {
    return zone.props.find((prop) => {
        if (auctionBiomeOf(prop) === undefined)
            return false;
        const box = silhouetteOf(prop);
        return at.x >= box.x0 && at.x <= box.x1 && at.y >= box.y0 && at.y <= box.y1;
    });
}
/** A player must stand in front of a hall's south-facing door to trade. */
export function auctionHouseInReach(zone, at) {
    return zone.props.find((prop) => auctionBiomeOf(prop) !== undefined &&
        at.y >= prop.y + 1 &&
        Math.max(Math.abs(at.x - prop.x), Math.abs(at.y - (prop.y + 1))) <= 2);
}
/** The signpost beside every hall's door (`data/mobs.ts`), clicked like a keeper. */
export const AUCTION_SIGN_MOB_ID = 'auction-sign';
/** The hall a signpost stands at: the nearest one, its door a few tiles away. */
export function auctionHouseNear(zone, at) {
    let best;
    let bestDistance = 5;
    for (const prop of zone.props) {
        if (auctionBiomeOf(prop) === undefined)
            continue;
        const distance = Math.max(Math.abs(at.x - prop.x), Math.abs(at.y - (prop.y + 1)));
        if (distance < bestDistance) {
            best = prop;
            bestDistance = distance;
        }
    }
    return best;
}
/** What the house keeps of a sale: a share of the price, one coin at least. */
export function auctionFee(price) {
    return Math.max(1, Math.round(price * AUCTION_FEE_RATE));
}
/** What the seller receives: the price minus the fee, never below zero. */
export function sellerProceeds(price) {
    return Math.max(0, price - auctionFee(price));
}
/** Like a trade (ADR 0034, 0044): anything unbound and past its purchase cooldown. */
export function isAuctionable(instance, now = Date.now()) {
    return tradeBlock(instance, now) === null;
}
/** Lower-cased, unaccented, single-spaced: how two names are compared. */
export function searchKey(text) {
    return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
}
/** Whether a template answers a search; an empty query matches everything. */
export function matchesAuctionQuery(template, query) {
    if (query.kind !== undefined && template.kind !== query.kind)
        return false;
    if (query.slot !== undefined && template.slot !== query.slot)
        return false;
    const q = query.q === undefined ? '' : searchKey(query.q);
    return q.length === 0 || searchKey(template.name).includes(q);
}
/** The template ids a search may find, for the SQL `IN`; empty when nothing matches. */
export function auctionSearchIds(query) {
    const ids = [];
    for (const template of ITEMS.values()) {
        if (matchesAuctionQuery(template, query))
            ids.push(template.id);
    }
    return ids;
}
//# sourceMappingURL=auction.js.map